import {
  CircularProgress,
  ThemeProvider,
} from '@ace-clublabs/shared-component-library';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { RouterProvider } from 'react-router-dom';
import ErrorPage from '@/components/shared/ErrorPage';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { queryClient } from '@/lib/queryClient';
import { initLogCannon } from '@/providers/log-cannon';
import { createRouter } from '@/routes';
import useThemeStore from '@/store/ThemeStore';
import { lightTheme, luxuryTheme } from '@/theming';

export const ErrorFallback = ({ resetErrorBoundary }: FallbackProps) => {
  return <ErrorPage onClickTryAgain={resetErrorBoundary} />;
};

export const AppProvider = () => {
  const [hasSetTheme, setHasSetTheme] = useState(false);
  const { setTheme } = useThemeStore();

  const path = location.pathname;
  const isLuxuryPath = useMemo(() => path.includes('luxury'), [path]);

  const featureFlags = useFeatureFlags();

  const router = useMemo(() => {
    if (!hasSetTheme) {
      return null;
    }
    return createRouter(isLuxuryPath, featureFlags);
  }, [isLuxuryPath, featureFlags, hasSetTheme]);

  useEffect(() => {
    initLogCannon();
  }, []);

  useEffect(() => {
    setTheme(isLuxuryPath ? 'luxury' : 'regular');
    setHasSetTheme(true);
  }, [setTheme, isLuxuryPath]);

  const fallback = (
    <div className='flex items-center justify-center w-screen h-screen'>
      <CircularProgress />
    </div>
  );

  if (!hasSetTheme || !router) {
    return fallback;
  }

  return (
    <React.Suspense fallback={fallback}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <QueryClientProvider client={queryClient}>
          {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools />}
          <ThemeProvider theme={isLuxuryPath ? luxuryTheme : lightTheme}>
            <RouterProvider router={router} />
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
